import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  BarChart,
  Badge,
  Card,
  Kpi,
  Loaded,
  Meter,
  Notice,
  Page,
  StackedBar,
  Table,
} from '../components/ui'
import { get } from '../lib/api'
import { count, dateTime, money, ms, percent, titleCase, tokens } from '../lib/format'
import { P, useAuth } from '../lib/auth'
import { useResource } from '../lib/useResource'

type Series = {
  days: number
  series: { day: string; cost_usd: number; requests: number }[]
  cycle_spend_usd: number
  cycle_budget_usd: number | null
  projected_cycle_usd: number
  p50_latency_ms: number
  p95_latency_ms: number
  cached_tokens: number
  input_tokens: number
  error_taxonomy: Record<string, number>
}

type Summary = {
  window_days: number
  total_cost_usd: number
  total_requests: number
  by_provider: Record<
    string,
    { cost_usd: number; requests: number; input_tokens: number; output_tokens: number }
  >
}

type RequestRow = {
  id: string
  created_at: string | null
  user_id: string
  feature: string
  task_class: string
  provider: string
  model_id: string
  runner_up_model_id: string | null
  routing_reason: string
  redaction_policy: string
  redactions: number
  failed_over_from: string | null
  error: string | null
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  cost_usd: number
  latency_ms: number
  prompt_available: boolean
  prompt_text?: string
}

export function AiUsage() {
  const { can } = useAuth()
  const [days, setDays] = useState(14)
  const [onlyErrors, setOnlyErrors] = useState(false)

  const series = useResource(
    (signal) => get<Series>('/v1/admin/ai/usage/series', { days }, signal),
    [days],
  )
  const summary = useResource(
    (signal) => get<Summary>('/v1/admin/ai/usage', { days }, signal),
    [days],
  )
  const requests = useResource(
    (signal) =>
      get<RequestRow[]>('/v1/admin/ai/requests', { limit: 100, only_errors: onlyErrors }, signal),
    [onlyErrors],
  )

  return (
    <Page
      title="AI usage"
      subtitle="The cost centre. Every figure is metered from actual provider-reported token counts, not estimated from request volume."
      actions={
        <select
          className="select"
          style={{ width: 160 }}
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
          aria-label="Window"
        >
          {[7, 14, 30, 90].map((n) => (
            <option key={n} value={n}>
              Last {n} days
            </option>
          ))}
        </select>
      }
    >
      <Loaded resource={series}>
        {(data) => {
          const cacheRate =
            data.input_tokens > 0 ? data.cached_tokens / data.input_tokens : 0
          const overBudget =
            data.cycle_budget_usd !== null && data.projected_cycle_usd > data.cycle_budget_usd
          return (
            <>
              {overBudget && (
                <Notice tone="warn">
                  At the current rate this cycle lands at{' '}
                  <strong>{money(data.projected_cycle_usd)}</strong> against a{' '}
                  {money(data.cycle_budget_usd)} cap. The projection assumes the rest of the month
                  looks like the part already observed — it is a straight line, not a forecast.
                </Notice>
              )}

              <div className="grid grid--kpi">
                <Kpi label="Spend this cycle" value={money(data.cycle_spend_usd)} tone="green" />
                <Kpi
                  label="Projected cycle"
                  value={money(data.projected_cycle_usd)}
                  tone={overBudget ? 'alert' : undefined}
                  foot={
                    data.cycle_budget_usd === null
                      ? 'No global cap set'
                      : `Cap ${money(data.cycle_budget_usd)}`
                  }
                />
                <Kpi label="p50 first token" value={ms(data.p50_latency_ms)} />
                <Kpi label="p95 first token" value={ms(data.p95_latency_ms)} tone="cyan" />
                <Kpi
                  label="Cache hit rate"
                  value={percent(cacheRate)}
                  tone="lime"
                  foot={`${tokens(data.cached_tokens)} of ${tokens(data.input_tokens)} input tokens`}
                />
                <Kpi
                  label="Errors in window"
                  value={count(Object.values(data.error_taxonomy).reduce((a, b) => a + b, 0))}
                  tone="orange"
                />
              </div>

              {data.cycle_budget_usd !== null && (
                <Card title="Budget burn-down" hint="Cycle spend against the global cap">
                  <div className="stack">
                    <Meter
                      value={data.cycle_spend_usd}
                      max={data.cycle_budget_usd}
                      tone={overBudget ? 'var(--alert)' : undefined}
                    />
                    <div className="row" style={{ fontSize: 11.5 }}>
                      <span className="muted">{money(data.cycle_spend_usd)} spent</span>
                      <div className="spacer" />
                      <span className="muted">{money(data.cycle_budget_usd)} cap</span>
                    </div>
                  </div>
                </Card>
              )}

              <div className="grid grid--2">
                <Card title={`Daily spend · ${data.days} days`}>
                  <BarChart
                    data={data.series.map((point) => ({
                      label: point.day.slice(5),
                      value: point.cost_usd,
                    }))}
                    format={(v) => money(v)}
                  />
                </Card>
                <Card title={`Request volume · ${data.days} days`}>
                  <BarChart
                    color="var(--cyan)"
                    data={data.series.map((point) => ({
                      label: point.day.slice(5),
                      value: point.requests,
                    }))}
                  />
                </Card>
              </div>
            </>
          )
        }}
      </Loaded>

      <div className="grid grid--2">
        <Card title="By provider" hint={`Window: ${summary.data?.window_days ?? days} days`}>
          <Loaded resource={summary}>
            {(data) => (
              <div className="stack">
                <StackedBar
                  segments={Object.entries(data.by_provider).map(([provider, bucket]) => ({
                    label: titleCase(provider),
                    value: bucket.cost_usd,
                  }))}
                  format={(v) => money(v)}
                />
                <Table
                  rows={Object.entries(data.by_provider).map(([provider, bucket]) => ({
                    provider,
                    ...bucket,
                  }))}
                  rowKey={(row) => row.provider}
                  empty="No provider traffic in this window."
                  columns={[
                    { key: 'p', header: 'Provider', strong: true, render: (r) => titleCase(r.provider) },
                    { key: 'r', header: 'Requests', render: (r) => count(r.requests) },
                    { key: 'i', header: 'In', render: (r) => tokens(r.input_tokens) },
                    { key: 'o', header: 'Out', render: (r) => tokens(r.output_tokens) },
                    { key: 'c', header: 'Cost', render: (r) => <span className="num">{money(r.cost_usd)}</span> },
                  ]}
                />
              </div>
            )}
          </Loaded>
        </Card>

        <Card title="Error taxonomy" hint="Grouped by the first segment of the error class">
          <Loaded resource={series}>
            {(data) =>
              Object.keys(data.error_taxonomy).length === 0 ? (
                <div className="empty">No gateway errors in the window.</div>
              ) : (
                <BarChart
                  color="var(--alert-bright)"
                  data={Object.entries(data.error_taxonomy).map(([kind, n]) => ({
                    label: kind,
                    value: n,
                  }))}
                />
              )
            }
          </Loaded>
        </Card>
      </div>

      <Card
        title="Request log"
        hint={
          can(P.aiPromptRead)
            ? 'You hold ai:prompt:read. Prompt text is included and each read is written to the audit log.'
            : 'Routing and cost only. Prompt content needs ai:prompt:read, which no default role holds — including admin.'
        }
        action={
          <label className="row" style={{ gap: 8, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(event) => setOnlyErrors(event.target.checked)}
            />
            Errors only
          </label>
        }
        flush
      >
        <Loaded resource={requests}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              empty={onlyErrors ? 'No failed requests.' : 'No co-pilot requests yet.'}
              columns={[
                { key: 'at', header: 'When', render: (r) => <span className="dim">{dateTime(r.created_at)}</span> },
                {
                  key: 'user',
                  header: 'Account',
                  render: (r) => (
                    <Link className="mono" to={`/users/${r.user_id}`}>
                      {r.user_id.slice(0, 8)}…
                    </Link>
                  ),
                },
                { key: 'task', header: 'Task', strong: true, render: (r) => titleCase(r.task_class) },
                {
                  key: 'model',
                  header: 'Chosen',
                  render: (r) => (
                    <span>
                      <span className="mono">{r.model_id}</span>
                      <span className="dim" style={{ display: 'block', fontSize: 10.5 }}>
                        {r.runner_up_model_id ? `runner-up ${r.runner_up_model_id}` : 'no runner-up'}
                      </span>
                    </span>
                  ),
                },
                {
                  key: 'why',
                  header: 'Why',
                  render: (r) => (
                    <span className="dim" style={{ fontSize: 11 }}>
                      {r.routing_reason || '—'}
                    </span>
                  ),
                },
                {
                  key: 'red',
                  header: 'Redactions',
                  render: (r) => (
                    <span title={`Policy: ${r.redaction_policy}`}>
                      <span className="num">{r.redactions}</span>{' '}
                      <span className="dim" style={{ fontSize: 10.5 }}>
                        {r.redaction_policy}
                      </span>
                    </span>
                  ),
                },
                { key: 'cost', header: 'Cost', render: (r) => <span className="num">{money(r.cost_usd)}</span> },
                { key: 'lat', header: 'Latency', render: (r) => <span className="dim">{ms(r.latency_ms)}</span> },
                {
                  key: 'state',
                  header: 'Result',
                  render: (r) =>
                    r.error ? (
                      <Badge tone="alert" >{r.error.split(':')[0]}</Badge>
                    ) : r.failed_over_from ? (
                      <Badge tone="orange">Failed over</Badge>
                    ) : (
                      <Badge tone="green">OK</Badge>
                    ),
                },
                ...(can(P.aiPromptRead)
                  ? [
                      {
                        key: 'prompt',
                        header: 'Prompt',
                        render: (r: RequestRow) =>
                          r.prompt_text ? (
                            <span style={{ maxWidth: 280, display: 'block' }} className="dim">
                              {r.prompt_text.slice(0, 120)}
                              {r.prompt_text.length > 120 ? '…' : ''}
                            </span>
                          ) : (
                            <span className="dim">not retained</span>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Loaded>
      </Card>
    </Page>
  )
}
